import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { HexAlphaColorPicker } from 'react-colorful'
import type { QrTagEventTypeEntry } from '../../../constants/Constants'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  createGroupEventType,
  deleteGroupEventType,
  fetchGroupEventTypes,
  updateGroupEventType,
} from '../../../modules/QrTagEventTypes'
import {
  fromHexRgba,
  parseHexColor,
  toCssColor,
  toHexRgba,
} from '../../../utils/colorUtils'
import { getErrorString } from '../../../utils/errorUtils'
import './QrTagEventTypesEditor.css'

type Props = {
  groups: IGroup[]
}

type DraftEventType = {
  key: string
  id: number | null
  name: string
  color: string | null
}

const QrTagEventTypesEditor = ({
  groups,
}: Props) => {
  const dispatch = useAppDispatch()
  const idRef = useRef(0)
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | null>(() => (
    groups[0]?.id ?? null
  ))
  const [draftEventTypes, setDraftEventTypes] = useState<DraftEventType[]>([])
  const [originalEventTypes, setOriginalEventTypes] = useState<QrTagEventTypeEntry[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [activePickerKey, setActivePickerKey] = useState<string | null>(null)

  const eventTypesEntry = useAppSelector((state) => (
    selectedGroupId ? state.qrTagEventTypes.entries[selectedGroupId] : undefined
  ))
  const eventTypes = useMemo(() => eventTypesEntry?.values ?? [], [eventTypesEntry])
  const status = eventTypesEntry?.status ?? 'idle'
  const error = eventTypesEntry?.error ?? null
  const isLoading = status === 'loading'

  const selectedGroup = useMemo(() => (
    selectedGroupId ? groups.find((group) => group.id === selectedGroupId) ?? null : null
  ), [groups, selectedGroupId])

  const createDraftEntry = useCallback(
    (entry?: QrTagEventTypeEntry): DraftEventType => ({
      key: entry ? `event-type-${entry.id}` : `event-type-new-${idRef.current++}`,
      id: entry?.id ?? null,
      name: entry?.name ?? '',
      color: entry?.color ?? null,
    }),
    [idRef],
  )

  const buildDraftList = useCallback((entries: QrTagEventTypeEntry[]) => {
    if (entries.length === 0) return []
    return entries.map((entry) => createDraftEntry(entry))
  }, [createDraftEntry])

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null)
      setDraftEventTypes([])
      return
    }

    setSelectedGroupId((prev) => {
      if (prev && groups.some((group) => group.id === prev)) return prev
      return groups[0]?.id ?? null
    })
  }, [groups])

  useEffect(() => {
    setDraftEventTypes([])
    setOriginalEventTypes([])
    setIsDirty(false)
    setSaveMessage(null)
    setSaveError(null)
    setActivePickerKey(null)
  }, [selectedGroupId])

  useEffect(() => {
    if (!selectedGroupId) return
    dispatch(fetchGroupEventTypes({ groupId: selectedGroupId }))
  }, [dispatch, selectedGroupId])

  useEffect(() => {
    if (!selectedGroupId || isDirty) return
    setOriginalEventTypes(eventTypes)
    setDraftEventTypes(buildDraftList(eventTypes))
  }, [buildDraftList, eventTypes, isDirty, selectedGroupId])

  const markDirty = useCallback(() => {
    if (!isDirty) setIsDirty(true)
    if (saveMessage) setSaveMessage(null)
    if (saveError) setSaveError(null)
  }, [isDirty, saveError, saveMessage])

  const handleAddRow = useCallback(() => {
    setDraftEventTypes((prev) => [...prev, createDraftEntry()])
    markDirty()
  }, [createDraftEntry, markDirty])

  const handleRemoveRow = useCallback((key: string) => {
    setDraftEventTypes((prev) => prev.filter((entry) => entry.key !== key))
    markDirty()
  }, [markDirty])

  const handleChangeName = useCallback((key: string, value: string) => {
    setDraftEventTypes((prev) => prev.map((entry) => (
      entry.key === key ? { ...entry, name: value } : entry
    )))
    markDirty()
  }, [markDirty])

  const handleChangeColor = useCallback((key: string, value: string) => {
    setDraftEventTypes((prev) => prev.map((entry) => {
      if (entry.key !== key) return entry
      return { ...entry, color: value }
    }))
    markDirty()
  }, [markDirty])

  const handleClearColor = useCallback((key: string) => {
    setDraftEventTypes((prev) => prev.map((entry) => (
      entry.key === key ? { ...entry, color: null } : entry
    )))
    markDirty()
  }, [markDirty])

  const handleTogglePicker = useCallback((key: string) => {
    setActivePickerKey((prev) => (prev === key ? null : key))
  }, [])

  const handleReload = useCallback(() => {
    if (!selectedGroupId) return
    setIsDirty(false)
    setSaveMessage(null)
    setSaveError(null)
    dispatch(fetchGroupEventTypes({ groupId: selectedGroupId, force: true }))
  }, [dispatch, selectedGroupId])

  const handleSave = useCallback(async () => {
    if (!selectedGroupId) return

    setIsSaving(true)
    setSaveMessage(null)
    setSaveError(null)

    try {
      const trimmedEntries = draftEventTypes
        .map((entry) => {
          const name = entry.name.trim()
          if (name.length === 0) return null
          const colorValue = entry.color?.trim() ?? ''
          return {
            id: entry.id,
            name,
            color: colorValue.length > 0 ? colorValue : null,
          }
        })
        .filter((entry): entry is {
          id: number | null
          name: string
          color: string | null
        } => Boolean(entry))
      const desiredEntries = trimmedEntries.map((entry, index) => ({
        ...entry,
        position: index,
      }))
      const desiredIds = new Set(
        desiredEntries
          .filter((entry): entry is {
            id: number
            name: string
            color: string | null
            position: number
          } => entry.id !== null)
          .map((entry) => entry.id),
      )
      const originalById = new Map(originalEventTypes.map((entry) => [entry.id, entry]))
      const deleteTargets = originalEventTypes.filter((entry) => !desiredIds.has(entry.id))

      const deletePromises = deleteTargets.map((target) => (
        dispatch(deleteGroupEventType({
          groupId: selectedGroupId,
          id: target.id,
        })).unwrap()
      ))
      await Promise.all(deletePromises)

      const updatePromises = desiredEntries.reduce<Array<Promise<unknown>>>((acc, entry) => {
        if (entry.id === null) return acc
        const original = originalById.get(entry.id)
        if (!original) return acc
        const updates: { name?: string; color?: string | null; position?: number } = {}
        if (original.name !== entry.name) updates.name = entry.name
        if ((original.color ?? null) !== entry.color) updates.color = entry.color
        if (original.position !== entry.position) updates.position = entry.position
        if (Object.keys(updates).length === 0) return acc
        acc.push(dispatch(updateGroupEventType({
          groupId: selectedGroupId,
          id: entry.id,
          ...updates,
        })).unwrap())
        return acc
      }, [])
      await Promise.all(updatePromises)

      const createPromises = desiredEntries
        .filter((entry) => entry.id === null)
        .map((entry) => dispatch(createGroupEventType({
          groupId: selectedGroupId,
          name: entry.name,
          color: entry.color,
          position: entry.position,
        })).unwrap())
      await Promise.all(createPromises)

      setSaveMessage('イベントタイプを更新しました。')
      setIsDirty(false)
      dispatch(fetchGroupEventTypes({ groupId: selectedGroupId, force: true }))
    } catch (err) {
      const message = (err && typeof err === 'object' && 'error' in err)
        ? String((err as { error?: unknown }).error ?? '')
        : ''
      setSaveError(message || getErrorString(err))
    } finally {
      setIsSaving(false)
    }
  }, [dispatch, draftEventTypes, originalEventTypes, selectedGroupId])

  if (groups.length === 0) return null

  return (
    <section className="qr-tag-event-types">
      <div className="qr-tag-event-types__header">
        <div>
          <h3 className="qr-tag-event-types__title">イベント種別の設定</h3>
          <p className="qr-tag-event-types__description">
            QRタグのイベント記録で使用する種別とカラーをグループごとに設定できます。
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleReload}
          disabled={!selectedGroupId || isLoading || isSaving}
        >
          再読み込み
        </button>
      </div>
      <div className="form-group qr-tag-event-types__group">
        <label className="form-label" htmlFor="qr-tag-event-types-group">
          対象グループ
        </label>
        <select
          id="qr-tag-event-types-group"
          className="form-control"
          value={selectedGroupId ?? ''}
          onChange={(event) => {
            const nextId = event.target.value ? Number(event.target.value) : null
            setSelectedGroupId(nextId as GroupId | null)
          }}
        >
          {groups.map((group) => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>
      </div>
      <p className="qr-tag-event-types__note">
        カラーと不透明度を選択できます。未設定にする場合は「クリア」を押してください。
      </p>
      {selectedGroup && isLoading && (
        <div className="qr-tag-event-types__message">
          {selectedGroup.name}
          のイベント種別を読み込み中です...
        </div>
      )}
      {error && (
        <div className="qr-tag-event-types__message qr-tag-event-types__message--error">
          {error}
        </div>
      )}
      <div className="qr-tag-event-types__table">
        <div className="qr-tag-event-types__row qr-tag-event-types__row--header">
          <span>イベント名</span>
          <span>カラー</span>
          <span>操作</span>
        </div>
        {draftEventTypes.length === 0 ? (
          <div className="qr-tag-event-types__empty">
            イベント種別が未設定です。行を追加してください。
          </div>
        ) : (
          draftEventTypes.map((entry, index) => {
            const parsedColor = parseHexColor(entry.color)
            const previewColor = toCssColor(entry.color)
            const pickerValue = toHexRgba(entry.color)
            const pickerId = `qr-tag-event-color-picker-${entry.key}`
            return (
              <div className="qr-tag-event-types__row" key={entry.key}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="例：紛失"
                  value={entry.name}
                  onChange={(event) => handleChangeName(entry.key, event.target.value)}
                  aria-label={`イベント種別 ${index + 1} 名称`}
                />
                <div className="qr-tag-event-types__color">
                  <button
                    type="button"
                    className="qr-tag-event-types__color-button"
                    onClick={() => handleTogglePicker(entry.key)}
                    aria-expanded={activePickerKey === entry.key}
                    aria-controls={pickerId}
                  >
                    <span
                      className={`qr-tag-event-types__color-dot${parsedColor ? '' : ' qr-tag-event-types__color-dot--empty'}`}
                      style={previewColor ? { backgroundColor: previewColor } : undefined}
                      aria-hidden="true"
                    />
                    カラーを選択
                  </button>
                  {activePickerKey === entry.key && (
                    <div className="qr-tag-event-types__picker" id={pickerId}>
                      <HexAlphaColorPicker
                        color={pickerValue}
                        onChange={(nextColor) => {
                          const converted = fromHexRgba(nextColor)
                          if (!converted) return
                          handleChangeColor(entry.key, converted)
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setActivePickerKey(null)}
                      >
                        閉じる
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => handleClearColor(entry.key)}
                    disabled={!parsedColor}
                  >
                    クリア
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleRemoveRow(entry.key)}
                >
                  削除
                </button>
              </div>
            )
          })
        )}
      </div>
      <div className="qr-tag-event-types__actions">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleAddRow}
          disabled={!selectedGroupId}
        >
          行を追加
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!selectedGroupId || isSaving || isLoading || !isDirty}
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
      </div>
      {saveMessage && (
        <div className="qr-tag-event-types__message qr-tag-event-types__message--success">
          {saveMessage}
        </div>
      )}
      {saveError && (
        <div className="qr-tag-event-types__message qr-tag-event-types__message--error">
          {saveError}
        </div>
      )}
    </section>
  )
}

export default QrTagEventTypesEditor
